import React, { useState, useEffect } from 'react';
import ConfirmationModal from './ConfirmationModal';
import { fetchWithAuth } from '../util/fetchWithAuth';
import Modal from 'react-modal';
import styles from './UpdateOrderStatusModal.module.css';
import { toast } from 'react-toastify';
import { statusColorMap } from '../util/statusColors';
import { OrderReferralDiscount } from '../types';
import { formatDiscount } from '../util/referralDiscount';
interface UpdateOrderStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  orderId: number;
  hasUnpaidExtraIncome?: boolean;
  referralDiscount?: Pick<OrderReferralDiscount, 'status' | 'amount'> | null;
  customerName?: string;
}

const REINSTATE = 'reinstated';

const optionLabel = (status: string) =>
  status === REINSTATE
    ? 'Reinstate (back to Submitted)'
    : status.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

const UpdateOrderStatusModal: React.FC<UpdateOrderStatusModalProps> = ({ isOpen, onClose, onSuccess, orderId, hasUnpaidExtraIncome, referralDiscount, customerName }) => {
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentStatus, setCurrentStatus] = useState('');
  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  const [showUpdateConfirmModal, setShowUpdateConfirmModal] = useState(false);
  const [showNotificationConfirmModal, setShowNotificationConfirmModal] = useState(false);
  const [reason, setReason] = useState('');
  const isReinstating = selectedStatus === REINSTATE;
  const canSubmit = !isReinstating || reason.trim() !== '';

  useEffect(() => {
    const fetchTransitions = async () => {
      if (isOpen && orderId) {
        try {
          const res = await fetchWithAuth(`/orders/${orderId}/available_transitions`);
          const data = await res.json();
          setStatusOptions(data.available_transitions || []);
          setCurrentStatus(data.current_status || '');
        } catch (error) {
          toast.error('Failed to fetch status transitions. Please try again.');
        }
      }
    };

    fetchTransitions();
  }, [isOpen, orderId]);

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedStatus(e.target.value);
  };

  const handleSubmit = () => {
    setShowUpdateConfirmModal(true);
  };

  const handleUpdateConfirm = () => {
    setShowUpdateConfirmModal(false);
    if (isReinstating) {
      handleFinalConfirm(false);
      return;
    }
    setShowNotificationConfirmModal(true);
  };

  const handleFinalConfirm = async (sendNotification: boolean) => {
    setShowNotificationConfirmModal(false);
    try {
      const body = isReinstating
        ? { status: selectedStatus, reason: reason.trim(), send_notification: false }
        : { status: selectedStatus, send_notification: sendNotification };
      const response = await fetchWithAuth(`/orders/${orderId}/update_status`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      if (!response.ok) return;
      setSelectedStatus('');
      setReason('');
      onSuccess();
      onClose();
      toast.success(
        isReinstating
          ? 'Order reinstated. It is back to Submitted.'
          : `Order status successfully updated to ${statusColorMap[selectedStatus].text}.`
      );
    } catch (error) {
      toast.error('Failed to update order status. Please try again.');
    }
  };

  const onCloseModal = () => {
    setSelectedStatus('');
    setReason('');
    onClose();
  }

  if (!isOpen) return null;

  return (
    <>
      <Modal isOpen={isOpen} onRequestClose={onClose} className={styles.modalContent}>
      <h2 className="text-2xl font-bold mb-4">{'Update Order Status'}</h2>
      <div className="space-y-4">
        {hasUnpaidExtraIncome && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-amber-800">
                  Warning: Unpaid Extra Income
                </h3>
                <div className="mt-2 text-sm text-amber-700">
                  <p>This order has unpaid extra income items. Please ensure they are resolved before updating the status.</p>
                </div>
              </div>
            </div>
          </div>
        )}
        <select
        value={selectedStatus}
        onChange={handleStatusChange}
        className="w-full p-2 border rounded-md"
        >
        <option value="">Select New Status</option>
        {statusOptions.map((status) => (
            <option key={status} value={status}>
            {optionLabel(status)}
            </option>
        ))}
        </select>
        {isReinstating && (
          <>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-amber-800">
                    This order goes back to Submitted
                  </h3>
                  <div className="mt-2 text-sm text-amber-700">
                    <p>Its total is recalculated at full delivery and its payment link, if it has one, is regenerated. The customer is not sent a message.</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <label htmlFor="reinstate-reason" className="block text-sm font-medium text-gray-700 mb-1">
                Reason (required)
              </label>
              <textarea
                id="reinstate-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border rounded"
                placeholder="Why is this order being reinstated?"
              />
            </div>
          </>
        )}
        {selectedStatus === 'cancelled' && referralDiscount?.status === 'reserved' && (
          <div role="alert" className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm">
            <p className="font-medium text-amber-800">This order has a referral discount ({formatDiscount(referralDiscount.amount)}).</p>
            <p className="text-amber-700">
              Cancelling releases it back to {customerName ?? 'the customer'} so it can be used on their next order.
            </p>
          </div>
        )}
        <div className="flex justify-end space-x-2">
        <button
          onClick={onCloseModal}
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 disabled:opacity-50"
        >
          Update Status
        </button>
        </div>
      </div>
      </Modal>

      {showUpdateConfirmModal && (
      <ConfirmationModal
        isOpen={showUpdateConfirmModal}
        message={
          isReinstating
            ? `Are you sure you want to reinstate this order and move it from ${currentStatus.toUpperCase()} back to SUBMITTED?`
            : `Are you sure you want to update the order status from the ${currentStatus.toUpperCase()} to ${selectedStatus.toUpperCase()}
        ?`
        }
        onConfirm={handleUpdateConfirm}
        onCancel={() => setShowUpdateConfirmModal(false)}
      />
      )}

      {showNotificationConfirmModal && (
      <ConfirmationModal
        isOpen={showNotificationConfirmModal}
        message="Would you like to send a notification?"
        onConfirm={() => handleFinalConfirm(true)}
        onCancel={() => handleFinalConfirm(false)}
      />
      )}
    </>
  );
};

export default UpdateOrderStatusModal;
